import { getBackendBaseURL, getHeaders } from '@/config/request';
import { AuthInteractionRequiredError } from '@/errors/AuthInteractionRequiredError';
import { NetworkError } from '@/errors/NetworkError';
import { ParseError } from '@/errors/ParseError';
import { isRefreshableAuthStatus } from '@/utils/authStatus';
import {
  reportTerminalAuthRecovery,
  withAuthRequestTelemetry,
} from '@/utils/logrocket.utils';
import { refreshApiBearer, resolveApiBearer } from '@/utils/apiBearer';
import { isInteractionRequiredError } from '@/utils/tokenRefresh';

export const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;

export interface IGenericRetrieverOptions {
  url: string;
  method?: 'GET' | 'POST';
  jwt?: string;
  enabled?: boolean;
  allowCancel?: boolean;
  isBlob?: boolean;
  returnError?: boolean;
  requestTimeoutMs?: number;
}

function genericRetriever<SuccessResponse>({
  url,
  jwt,
  method = 'GET',
  enabled = true,
  isBlob = false,
  returnError = false,
  allowCancel = true,
  requestTimeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
}: IGenericRetrieverOptions): ({
  signal,
}: {
  signal: AbortSignal;
}) => Promise<SuccessResponse> {
  return async ({ signal }: { signal: AbortSignal }) => {
    if (!enabled) {
      return {} as SuccessResponse;
    }

    const timeoutSignal = AbortSignal.timeout(requestTimeoutMs);
    const requestSignal = allowCancel
      ? AbortSignal.any([signal, timeoutSignal])
      : timeoutSignal;

    const requestUrl = `${getBackendBaseURL()}${url}`;
    const executeRequest = async ({
      token,
      attempt,
    }: {
      token: string | undefined;
      attempt: 'initial' | 'retry';
    }): Promise<Response> => {
      const executeFetch = () =>
        fetch(requestUrl, {
          method,
          headers: getHeaders({ jwt: token }),
          signal: requestSignal,
        });

      return token
        ? withAuthRequestTelemetry(
            { url: requestUrl, method, attempt },
            executeFetch
          )
        : executeFetch();
    };

    let initialBearer: string | undefined;
    try {
      initialBearer = await resolveApiBearer(jwt, { signal: requestSignal });
    } catch (error) {
      if (isInteractionRequiredError(error)) {
        throw new AuthInteractionRequiredError();
      }
      throw error;
    }

    let response = await executeRequest({
      token: initialBearer,
      attempt: 'initial',
    });

    if (isRefreshableAuthStatus(response.status) && jwt) {
      const initialAuthStatus = response.status;
      let refreshedBearer: string | null;

      try {
        refreshedBearer = await refreshApiBearer({
          signal: requestSignal,
          currentIdToken: jwt,
        });
      } catch (error) {
        if (isInteractionRequiredError(error)) {
          reportTerminalAuthRecovery({
            status: initialAuthStatus,
            method,
            reason: 'interaction-required',
          });
          throw new AuthInteractionRequiredError();
        }
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          reportTerminalAuthRecovery({
            status: initialAuthStatus,
            method,
            reason: 'refresh-error',
          });
        }
        throw error;
      }

      if (refreshedBearer) {
        response = await executeRequest({
          token: refreshedBearer,
          attempt: 'retry',
        });
      } else {
        reportTerminalAuthRecovery({
          status: initialAuthStatus,
          method,
          reason: 'refresh-unavailable',
        });
      }
    }

    if (!response.ok && (response.status === 401 || response.status === 403)) {
      let errorMessage = 'Network response was not ok';

      try {
        const errorResponse: unknown = JSON.parse(await response.text());
        if (
          typeof errorResponse === 'object' &&
          errorResponse !== null &&
          'error' in errorResponse &&
          typeof errorResponse.error === 'string'
        ) {
          errorMessage = errorResponse.error;
        }
      } catch {
        // The HTTP status remains authoritative when the body cannot be read or parsed.
      }

      throw new NetworkError(errorMessage, response.status);
    }

    let jsonResponse: SuccessResponse | undefined;

    if (!isBlob) {
      const textResponse = await response.text();
      try {
        if (textResponse) {
          jsonResponse = JSON.parse(textResponse) as SuccessResponse;
        }
      } catch {
        throw new ParseError(
          'BE response is not a valid JSON',
          textResponse,
          response.status
        );
      }
    }

    if (!response.ok && !returnError) {
      throw new NetworkError(
        !isBlob &&
          jsonResponse &&
          typeof jsonResponse === 'object' &&
          'error' in (jsonResponse as object)
          ? (jsonResponse as unknown as { error: string }).error
          : 'Network response was not ok',
        response.status
      );
    }

    if (isBlob) {
      return (await response.blob()) as SuccessResponse;
    }

    if (jsonResponse === undefined) {
      return {} as SuccessResponse;
    }

    return jsonResponse;
  };
}

export default genericRetriever;
